/**
 * Shape of one entry in assets/generated/cards.json, as written by
 * tools/fetch-cards.mjs from Riot's card gallery.
 */
export interface CardData {
  /** Collector code, e.g. "UNL-131". Signed printings get an "s" suffix. */
  id: string;
  set: string;
  setName: string;
  /** 'T' for a printed token, 'R' for a basic rune, 'SP' promo, null otherwise. */
  codePrefix: string | null;
  number: number;
  /** Suffix on an alternate-art printing ("a"), otherwise null. */
  variant: string | null;
  signed: boolean;
  collector: string;
  name: string;
  type: CardType;
  /** 133.7. Champion and Signature apply across card types. */
  superType: SuperType | null;
  rarity: Rarity;
  domains: Domain[];
  /** 133.8. Champion tags, factions, species. */
  tags: string[];
  energy: number | null;
  power: number | null;
  might: number | null;
  /** 137. Only on equipment; modulates the Might of whatever it is attached to. */
  mightBonus: number | null;
  /** Rules text, with the game's symbols in the comprehensive rules' shorthand. */
  text: string | null;
  /** 136. Second text block, active only while this card is attached. */
  effect: string | null;
  /** Frame template whose foil mask this card uses, or null for unframed types. */
  frame: string | null;
  art: string;
  artUrl: string | null;
  orientation: string;
}

export type CardType =
  | 'unit' | 'champion' | 'spell' | 'signatureSpell'
  | 'gear' | 'legend' | 'battlefield' | 'rune' | 'token';

export type SuperType = 'champion' | 'signature' | 'basic' | 'token';

export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'showcase';

export type Domain = 'fury' | 'calm' | 'mind' | 'body' | 'chaos' | 'order';

// Sampled from the printed runes rather than remembered: Calm and Body were
// swapped here and in the HUD, so a Body card glowed green and wore a green
// pip against its orange frame.  holds this table to the art.
export const DOMAIN_COLORS: Record<Domain, readonly [number, number, number]> = {
  fury: [0.85, 0.22, 0.16],
  calm: [0.24, 0.62, 0.30],
  mind: [0.20, 0.40, 0.80],
  body: [0.88, 0.50, 0.20],
  chaos: [0.52, 0.24, 0.68],
  order: [0.86, 0.68, 0.18],
};

/**
 * How much foil a printing carries.
 *   0 none, 1 metal trim only, 2 rare holo, 3 showcase full-face.
 * Every Riftbound card has hot-stamped trim, so nothing sits at 0 -- tier 0
 * exists for proxies and placeholders.
 */
export function foilTier(rarity: Rarity): number {
  switch (rarity) {
    case 'common':
    case 'uncommon':
      return 1;
    case 'rare':
      return 2;
    case 'epic':
    case 'showcase':
      return 3;
  }
}

export const RARITY_ORDER: Record<Rarity, number> = {
  common: 0, uncommon: 1, rare: 2, epic: 3, showcase: 4,
};

/**
 * Which back a card is printed on.
 *
 * `dark` is the black stock the legends and battlefields use, `main` the deep
 * blue of the Main Deck, `rune` the white of the Rune Deck.
 */
export type CardBack = 'dark' | 'main' | 'rune';
